// ---------------------------------------------------------
// Datei: one360.ts
// Zweck: Kanonischer typisierter Axios-Client für alle /one360/*-Aufrufe. Andere
//        services/api/*.ts-Dateien bauen auf dieser Instanz auf (siehe CLAUDE.md).
// Kontext: Request-Interceptor hängt das Google-ID-Token an (bricht ohne Token
//          sofort ab, statt unauthentifiziert zu senden). Response-Interceptor
//          packt BaseResponse<T> aus, normalisiert Fehler und dispatcht
//          "auth:unauthorized" ausschließlich bei 401.
// Stand: 2026-07-15
// ---------------------------------------------------------
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getIdToken, isTokenExpired, setIdToken } from '../googleAuthSession'; // request interceptor

type TypedAxiosInstance = Omit<AxiosInstance, 'get' | 'post' | 'put' | 'patch' | 'delete'> & {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>;
};

const one360 = axios.create({
  baseURL: '/one360',
  timeout: 30_000,
}) as TypedAxiosInstance;

export const isAuthenticationFailure = (status: unknown): boolean => status === 401;

// Client-seitig verworfener Request (kein Token vorhanden), siehe Request-Interceptor unten.
// Kein echter 401 der API - Aufrufer sollen ihn still ignorieren statt eine Fehlermeldung zu
// zeigen, da AuthContext parallel bereits auf /login umleitet (siehe auth:unauthorized).
export const isAuthMissingError = (error: unknown): boolean =>
  !!(error && typeof error === 'object' && (error as { isAuthMissing?: boolean }).isAuthMissing);

// Bearer ID-Token setzen. Kein Token → Request sofort verwerfen statt ohne Auth rausschicken.
one360.interceptors.request.use((config) => {
  if (!config.headers['Authorization']) {
    const token = getIdToken();
    if (token && isTokenExpired(token)) {
      // Nach längerer Baustellenarbeit ist der UI-Login noch aktiv, das JWT aber
      // abgelaufen. Vor jedem API-Zugriff (auch Lesen) neu anmelden, wie bei einer
      // Server-401; lokale Queue-Daten bleiben erhalten. Tokens ohne exp sind ebenfalls
      // ungültig, da ihre Gültigkeitsdauer nicht geprüft werden kann.
      setIdToken(null);
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      return Promise.reject(Object.assign(new Error('Sitzung abgelaufen. Bitte erneut anmelden.'), {
        status: 401, isAuthMissing: true,
      }));
    }
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      return Promise.reject(
        Object.assign(new Error('Kein Auth-Token verfügbar'), { status: 401, isAuthMissing: true })
      );
    }
  }
  return config;
});

// BaseResponse<T> auspacken und Fehler normalisieren
one360.interceptors.response.use(
  (response) => {
    const body = response.data;
    return body?.data ?? body;
  },
  (error) => {
    if (axios.isCancel(error)) return Promise.reject(error);
    // isAuthMissing = vom Request-Interceptor gesetzt, kein Netzwerkfehler loggen
    if ((error as { isAuthMissing?: boolean }).isAuthMissing) return Promise.reject(error);
    const status = error.response?.status;
    // 403 bedeutet eine fachliche Berechtigungsprüfung der One360-API. Ein gültiger
    // Login darf dadurch nicht verworfen werden, sonst führt z.B. eine noch nicht
    // in DEV ausgerollte Serviceroute fälschlich zurück zur Anmeldeseite.
    if (isAuthenticationFailure(status)) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    // ApiControllerBase liefert fachliche 4xx-Fehler als Textantwort. Ohne diesen
    // Fall fiel die Meldung auf Axions generisches "Request failed ..." zurück.
    const responseBody = error.response?.data;
    const msg =
      (typeof responseBody === 'string' && responseBody.trim()) ||
      responseBody?.message ||
      (typeof responseBody?.error === 'string' ? responseBody.error : undefined) ||
      responseBody?.detail ||
      responseBody?.title ||
      error.message ||
      'One360-Anfrage fehlgeschlagen';
    return Promise.reject(Object.assign(new Error(msg), { status, code: typeof responseBody?.code === 'string' ? responseBody.code : undefined }));
  }
);

export default one360;
